'use client';

import { Suspense } from 'react';

import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';

import { cn } from '@packages/shared/src/utils/cn/cn';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { useIntl } from 'react-intl';
import { DynamicSearchHistory, SearchHistoryLoading } from './SearchHistory';
import {
  DynamicSearchTermSuggestions,
  SearchTermSuggestionsLoading,
} from './SearchTermSuggestions';
import type { SuggestionTrackingData } from './types';
import { bypassBlockMessages } from './messages';

export type SearchFieldPopupProps = {
  /** The current search term for suggestions */
  searchTerm?: string;
  /** 
   * Token for fetching data (will be removed when swr completely implements suspense support)
   @deprecated 
  */
  jwtToken: string;
  /** Callback that is called when the user clicks on any search suggestion */
  onAccept: (suggestionTrackingData: SuggestionTrackingData) => void;
  /** Callback that is called when the user clicks the arrow button to accept a search suggestion as the current input value without immediately triggering a navigation */
  onAdopt: (value: string) => void;
};

export const SearchFieldPopup = ({
  searchTerm,
  jwtToken,
  onAccept,
  onAdopt,
}: SearchFieldPopupProps) => {
  const { formatMessage } = useIntl();
  return (
    <div
      className={cn(
        `z-search-field-popup`, //  higher than the flyout navigation (filter popover is appbar which is 1100, appbar + 1 --> 1101 which flyout navigation)
        'bg-color-grey-light absolute top-full left-0 w-full py-0 shadow-xs',
        searchTerm && 'lg:w-[170%]', // it should not be md: otherwise the suggestion box will go off the viewport on smaller screens like (tabs, minitabs)
      )}
    >
      {!searchTerm && (
        <ErrorBoundary>
          <Suspense fallback={<SearchHistoryLoading />}>
            <BypassBlock
              buttonText={formatMessage(bypassBlockMessages.bypassSearchHistory)}
              skipScrollIntoView
            >
              <DynamicSearchHistory jwtToken={jwtToken} onAdopt={onAdopt} onAccept={onAccept} />
            </BypassBlock>
          </Suspense>
        </ErrorBoundary>
      )}
      {searchTerm && (
        <ErrorBoundary>
          <Suspense fallback={<SearchTermSuggestionsLoading />}>
            <DynamicSearchTermSuggestions
              jwtToken={jwtToken}
              onAccept={onAccept}
              searchTerm={searchTerm}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </div>
  );
};
